import React, { useMemo, useState } from 'react';
import { toast } from 'react-toastify';

import DropLoaderLarge from '@C/Utils/DropLoaderLarge';
import { useGlobalStore } from '@stores/globalStore';

import services from './data';
import {
  applyMapping,
  autoDetectMapping,
  CUSTOMER_FIELDS,
  currency,
  readSheet,
  readWorkbook,
  splitFullName,
} from './helpers';
import { ImportSummary } from './ImportSummary';
import { MappingPanel } from './MappingPanel';

const SUMMARY_COLUMNS = [
  { key: 'first_name', label: 'First name' },
  { key: 'last_name', label: 'Last name' },
  { key: 'mobile', label: 'Mobile' },
  { key: 'outstanding', label: 'Opening balance' },
];

const emptyCustomerPayload = (row) => ({
  first_name: row.first_name,
  last_name: row.last_name || '',
  password: '',
  is_password_update: false,
  mobile: row.mobile,
  email: row.email || '',
  gstn: row.gstn || '',
  pan: row.pan || '',
  user_role: 104,
  credit_customer: {
    notes: row.notes || '',
    vehicle: [
      {
        vehicle_no: row.vehicle_no || '',
        driver_name: '',
        mobile: '',
      },
    ],
    billing_cycle: {
      start_date: row.start_date,
      payment_period: row.payment_period,
      limit: currency(row.limit),
      outstanding: row.outstanding ?? 0,
    },
    address: {
      country: '',
      state: row.state || '',
      city: row.city || '',
      pincode: row.pincode || null,
      country_code: '',
      full_address: row.full_address || '',
    },
  },
});

const validateRow = (row) => {
  const missing = [];
  if (!row.first_name) missing.push('First name');
  if (!row.mobile || row.mobile.length !== 10) missing.push('Mobile number');
  if (!row.start_date) missing.push('Date');
  if (row.payment_period === null || row.payment_period === '')
    missing.push('Billing cycle');
  if (row.limit === null || row.limit === '') missing.push('Limit');
  if (row.outstanding === null || row.outstanding === '')
    missing.push('Opening balance');
  return missing;
};

const CreditCustomerImport = () => {
  const existingCustomers = useGlobalStore((state) => state.creditCustomers);
  const setCreditCustomers = useGlobalStore(
    (state) => state.setCreditCustomers,
  );

  const [fileName, setFileName] = useState('');
  const [headers, setHeaders] = useState([]);
  const [rawRows, setRawRows] = useState([]);
  const [mapping, setMapping] = useState({});
  const [defaults, setDefaults] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [results, setResults] = useState([]);

  const existingMobiles = useMemo(
    () =>
      new Set(
        (existingCustomers || []).map((item) => item.basic_details?.mobile),
      ),
    [existingCustomers],
  );

  const parsedRows = useMemo(
    () =>
      rawRows.map((row) => {
        const mapped = applyMapping(row, CUSTOMER_FIELDS, mapping, defaults);
        const sameColumn =
          mapping.first_name && mapping.first_name === mapping.last_name;
        const combinedName =
          !mapping.last_name && mapped.first_name?.includes(' ');
        if (sameColumn || combinedName) {
          const { first, last } = splitFullName(mapped.first_name);
          mapped.first_name = first;
          mapped.last_name = last;
        }
        return mapped;
      }),
    [rawRows, mapping, defaults],
  );

  const handleFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setResults([]);
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = (event) => {
      const workbook = readWorkbook(event.target.result);
      const { headers: sheetHeaders, rows } = readSheet(
        workbook,
        workbook.SheetNames[0],
      );
      setHeaders(sheetHeaders);
      setRawRows(rows);
      setMapping(autoDetectMapping(sheetHeaders, CUSTOMER_FIELDS));
      setDefaults({});
    };
    reader.readAsArrayBuffer(file);
  };

  const handleMappingChange = (key, value) => {
    setMapping((prev) => ({ ...prev, [key]: value }));
  };

  const handleDefaultChange = (key, value) => {
    setDefaults((prev) => ({ ...prev, [key]: value }));
  };

  const refreshCreditCustomersStore = async () => {
    try {
      const response = await services.getCreditCustomerList();
      setCreditCustomers(response?.data?.data || []);
    } catch (error) {
      console.log(error);
    }
  };

  const submitImport = async () => {
    setIsSubmitting(true);

    const seenMobiles = new Set();
    const skipped = [];
    const toImport = [];

    parsedRows.forEach((row, index) => {
      const rowNumber = index + 2;
      const missing = validateRow(row);
      if (missing.length > 0) {
        skipped.push({
          row: rowNumber,
          data: row,
          status: 'failed',
          message: `Missing required field(s): ${missing.join(', ')}`,
        });
        return;
      }
      if (existingMobiles.has(row.mobile)) {
        skipped.push({
          row: rowNumber,
          data: row,
          status: 'skipped',
          message: 'Customer already exists in Petrometer',
        });
        return;
      }
      if (seenMobiles.has(row.mobile)) {
        skipped.push({
          row: rowNumber,
          data: row,
          status: 'skipped',
          message: 'Duplicate mobile number in the uploaded file',
        });
        return;
      }
      seenMobiles.add(row.mobile);
      toImport.push({ row: rowNumber, data: row });
    });

    if (toImport.length === 0) {
      setResults(skipped);
      setIsSubmitting(false);
      if (skipped.length === 0) {
        toast.error('No rows found to import');
      }
      return;
    }

    try {
      const response = await services.bulkRegisterCustomers({
        user_list: toImport.map((item) => emptyCustomerPayload(item.data)),
      });

      const status = response.data.status;
      let imported = [];

      if (status === 226) {
        const failedMobiles = (response.data.data || []).map(String);
        imported = toImport.map((item) =>
          failedMobiles.includes(String(item.data.mobile))
            ? {
                row: item.row,
                data: item.data,
                status: 'failed',
                message:
                  response.data.message ||
                  'Already registered in another bunk',
              }
            : {
                row: item.row,
                data: item.data,
                status: 'added',
              },
        );
        toast.success(
          'Import finished. Some customers could not be added — see summary below.',
        );
      } else if (status === 208) {
        imported = toImport.map((item) => ({
          row: item.row,
          data: item.data,
          status: 'failed',
          message: response.data.message,
        }));
        toast.error(response.data.message || 'Import failed');
      } else {
        imported = toImport.map((item) => ({
          row: item.row,
          data: item.data,
          status: 'added',
        }));
        toast.success('Credit customers imported successfully');
      }

      setResults([...imported, ...skipped].sort((a, b) => a.row - b.row));
      if (status !== 208) {
        await refreshCreditCustomersStore();
        setRawRows([]);
        setHeaders([]);
        setFileName('');
      }
    } catch (error) {
      const message =
        error.response?.data?.message || 'Import failed. Please try again.';
      toast.error(message);
      setResults(
        [
          ...toImport.map((item) => ({
            row: item.row,
            data: item.data,
            status: 'failed',
            message,
          })),
          ...skipped,
        ].sort((a, b) => a.row - b.row),
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <section className="widget full-width form-table">
        <header>
          <h3>Import Credit Customers</h3>
          <div className="actions">
            <div className="input-wrapper file-upload">
              <input
                type="file"
                id="credit-customer-file"
                accept=".xlsx, .xls"
                onChange={handleFile}
              />
            </div>
          </div>
        </header>
        {fileName && (
          <main>
            <p className="migration-hint">
              {fileName} — {parsedRows.length} row(s) detected
            </p>
          </main>
        )}
      </section>

      {headers.length > 0 && (
        <>
          <MappingPanel
            fields={CUSTOMER_FIELDS}
            headers={headers}
            mapping={mapping}
            defaults={defaults}
            onChange={handleMappingChange}
            onDefaultChange={handleDefaultChange}
          />
          <div className="form-actions">
            <button
              className={`submit ${isSubmitting ? 'loading' : ''}`}
              disabled={isSubmitting}
              onClick={submitImport}
            >
              Import {parsedRows.length} customer(s)
            </button>
          </div>
        </>
      )}

      {isSubmitting && <DropLoaderLarge />}

      <ImportSummary
        title="Credit customer import summary"
        results={results}
        columns={SUMMARY_COLUMNS}
      />
    </>
  );
};

export { CreditCustomerImport };
